import { ChevronRight } from "@mui/icons-material";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

/**
 * Individual menu item for the expansive sidebar menu.
 * Supports recursive submenus and authorization checks.
 */
const IndieSideMenuItem = memo(({
    menuItem,
    t,
    handleClose,
    pathname,
    checkItemAuthorization
}) => {
    const navigate = useNavigate();
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
    const submenuOpen = Boolean(submenuAnchorEl);
    const [canHover, setCanHover] = useState(false);
    const timeoutRef = useRef(null);

    // Detect hover capability + viewport width
    useEffect(() => {
        const checkCapabilities = () => {
            const mediaQuery = window.matchMedia('(hover: hover)');
            const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isWide = window.innerWidth >= 1200;
            
            setCanHover(mediaQuery.matches && !isTouch && isWide);
        };

        checkCapabilities();

        const mediaQuery = window.matchMedia('(hover: hover)');
        mediaQuery.addEventListener('change', checkCapabilities);
        window.addEventListener('resize', checkCapabilities);

        return () => {
            mediaQuery.removeEventListener('change', checkCapabilities);
            window.removeEventListener('resize', checkCapabilities);
        };
    }, []);

    // Filter children based on permissions
    const authorizedChildren = useMemo(() => {
        return menuItem.children?.filter(child => checkItemAuthorization(child)) || [];
    }, [menuItem.children, checkItemAuthorization]);

    // Determine active state
    const isActive = useMemo(() => {
        const path = menuItem.path;
        if (!path) return false;
        return pathname.includes(path);
    }, [pathname, menuItem.path]);

    const color = isActive ? "primary.main" : "text.secondary";

    // Close submenus on navigation
    useEffect(() => {
        setSubmenuAnchorEl(null);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, [pathname]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleOpen = useCallback((e) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setSubmenuAnchorEl(e.currentTarget);
    }, []);

    const handleCloseInternal = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setSubmenuAnchorEl(null);
    }, []);

    const handleMouseEnter = useCallback((e) => {
        if (canHover && authorizedChildren.length > 0) {
            handleOpen(e);
        }
    }, [canHover, authorizedChildren.length, handleOpen]);

    const handleMouseLeave = useCallback(() => {
        if (canHover) {
            timeoutRef.current = setTimeout(() => {
                setSubmenuAnchorEl(null);
            }, 200);
        }
    }, [canHover]);

    const handleMenuEnter = useCallback(() => {
        if (canHover && timeoutRef.current) clearTimeout(timeoutRef.current);
    }, [canHover]);

    const handleClick = useCallback((e) => {
        if (menuItem.handler) {
            menuItem.handler();
            handleClose();
        } else if (authorizedChildren.length > 0) {
            // Toggle for mobile/touch
            if (!canHover) {
                setSubmenuAnchorEl(submenuAnchorEl ? null : e.currentTarget);
            }
        } else if (menuItem.path) {
            navigate(menuItem.path, { replace: true });
            handleClose();
        }
    }, [menuItem, handleClose, navigate, authorizedChildren.length, canHover, submenuAnchorEl]);

    const closeSubmenu = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setSubmenuAnchorEl(null);
    }, []);

    // Robust icon rendering logic
    const renderIcon = useMemo(() => {
        const icon = menuItem.icon;
        if (!icon) return null;

        // If it's a function (component) or a special React object (memo, forwardRef), render it as a tag
        if (typeof icon === "function" || (typeof icon === "object" && icon.$$typeof && !icon.props)) {
            const Icon = icon;
            return <Icon />;
        }

        // Otherwise assume it's already an element (like <Icon />) or a string
        return icon;
    }, [menuItem.icon]);

    return (
        <Fragment>
            <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} sx={{ mx: 1, borderRadius: "8px", overflow: "hidden" }}>
                <MenuItem
                    onClick={handleClick}
                    sx={{
                        px: 1.5,
                        py: 1.5,
                        minHeight: 44,
                        borderRadius: "8px"
                    }}
                >
                    {renderIcon && (
                        <ListItemIcon sx={{ minWidth: "auto", mr: 0.75, "& svg": { fontSize: 18, color: color } }}>
                            {renderIcon}
                        </ListItemIcon>
                    )}

                    <ListItemText
                        sx={{
                            color: color,
                            m: 0,
                            "& .MuiListItemText-primary": { fontSize: 13, fontWeight: 500 },
                        }}
                        primary={t(menuItem.name || menuItem.text)}
                    />

                    {authorizedChildren.length > 0 && (
                        <Box sx={{ ml: 2, display: "flex", alignItems: "center" }}>
                            <ChevronRight sx={{ fontSize: 18, color: color }} />
                        </Box>
                    )}
                </MenuItem>
            </Box>

            {authorizedChildren.length > 0 && (
                <Menu
                    anchorEl={submenuAnchorEl}
                    open={submenuOpen}
                    onClose={closeSubmenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    // Disable pointer events on the root Menu only on desktop to prevent flickering
                    sx={{ pointerEvents: canHover ? 'none' : 'auto' }}
                    slotProps={{
                        paper: {
                            sx: { mt: 0, ml: 0.5, boxShadow: 3, pointerEvents: 'auto' },
                            onMouseEnter: handleMenuEnter,
                            onMouseLeave: handleMouseLeave
                        }
                    }}
                >
                    {authorizedChildren.map((child, idx) => (
                        <IndieSideMenuItem
                            key={child.name || child.id || idx}
                            menuItem={child}
                            t={t}
                            handleClose={closeSubmenu}
                            pathname={pathname}
                            checkItemAuthorization={checkItemAuthorization}
                        />
                    ))}
                </Menu>
            )}
        </Fragment>
    );
});

export default IndieSideMenuItem;
